import React from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { getSubscriptions, updateSubscription, deleteSubscription } from "../../../data";
import { AdminLayout } from "../shared/layout";
import { useNavigate } from "react-router-dom";
import { Ban, Trash2 } from "lucide-react";
import { DataTable } from "@/components/table";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "@/components/ui/use-toast";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

export function SubscriptionListScreen() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { data, isLoading } = useQuery({
    queryKey: ["subscriptions"],
    queryFn: getSubscriptions,
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }) => updateSubscription(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries(["subscriptions"]);
      toast({
        title: "Başarılı",
        description: "Abonelik başarıyla güncellendi",
        variant: "success",
      });
    },
    onError: (error) => {
      toast({
        title: "Hata",
        description: "Abonelik güncellenirken bir hata oluştu",
        variant: "destructive",
      });
      console.error("Abonelik güncelleme hatası:", error);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: deleteSubscription,
    onSuccess: () => {
      queryClient.invalidateQueries(["subscriptions"]);
      toast({
        title: "Başarılı",
        description: "Abonelik başarıyla silindi",
        variant: "success",
      });
    },
    onError: (error) => {
      toast({
        title: "Hata",
        description: "Abonelik silinirken bir hata oluştu",
        variant: "destructive",
      });
      console.error("Abonelik silme hatası:", error);
    },
  });

  const handleCancel = (id) => {
    updateMutation.mutate({ id, data: { status: "CANCELED" } });
  };

  const handleDelete = (id) => {
    deleteMutation.mutate(id);
  };

  const columns = [
    { 
      key: "user", 
      label: "Kullanıcı", 
      render: (row) => row.user?.name || "Bilinmeyen Kullanıcı"
    },
    { 
      key: "plan", 
      label: "Plan", 
      render: (row) => row.plan?.name || "Bilinmeyen Plan"
    },
    { 
      key: "startDate", 
      label: "Başlangıç Tarihi", 
      render: (row) => new Date(row.startDate).toLocaleDateString()
    },
    { 
      key: "endDate", 
      label: "Bitiş Tarihi", 
      render: (row) => new Date(row.endDate).toLocaleDateString()
    },
    { 
      key: "status", 
      label: "Durum", 
      render: (row) => (
        <Badge variant={row.status === "ACTIVE" ? "success" : "secondary"}>
          {row.status === "ACTIVE" ? "Aktif" : "Pasif"}
        </Badge>
      )
    },
    { 
      key: "actions", 
      label: "İşlemler", 
      render: (row) => (
        <div className="flex justify-end space-x-2">
          {row.status === "ACTIVE" && (
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button 
                  variant="ghost" 
                  size="icon" 
                  className="hover:bg-yellow-100"
                  title="Aboneliği İptal Et"
                >
                  <Ban className="h-4 w-4 text-yellow-600" />
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Aboneliği İptal Et</AlertDialogTitle>
                  <AlertDialogDescription>
                    Bu aboneliği iptal etmek istediğinizden emin misiniz? Kullanıcı dönem sonuna kadar erişime sahip olabilir.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Vazgeç</AlertDialogCancel>
                  <AlertDialogAction onClick={() => handleCancel(row.id)}>İptal Et</AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          )}

          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button 
                variant="ghost" 
                size="icon" 
                className="hover:bg-red-100"
                title="Aboneliği Sil"
              >
                <Trash2 className="h-4 w-4 text-red-500" />
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Aboneliği Sil</AlertDialogTitle>
                <AlertDialogDescription>
                  Bu abonelik kaydını tamamen silmek istediğinizden emin misiniz? Bu işlem geri alınamaz.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Vazgeç</AlertDialogCancel>
                <AlertDialogAction onClick={() => handleDelete(row.id)}>Sil</AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>
      )
    },
  ];

  return (
    <AdminLayout
      title="Abonelikler"
      description="Kullanıcı aboneliklerini buradan yönetin."
      loading={isLoading}
    >
      {data && (
        <DataTable 
          data={Array.isArray(data) ? data : (data.data || [])} 
          columns={columns}
          title="Abonelik Listesi"
          enableSearch={true}
          enableSort={true}
          enablePagination={true}
          itemsPerPage={10}
        />
      )}
    </AdminLayout>
  );
}